import React from 'react';
import { useMutation, useQuery } from 'react-apollo';
import { makeStyles } from '@material-ui/core/styles';
import { LinearProgress } from '@material-ui/core';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Theme,
} from '@material-ui/core';
import { Field, Form, SelectField, TextField, useModal } from 'src/app-builder';

import { useNotification } from 'src/hooks';
import { SubmitButton } from 'src/components';
import { CompanyTeammateEditEventSchema } from 'src/utils/schemas/event';
import { EVENT_RESOLVER_MUTATION, EventType, TEAM_EDIT_QUERY, User } from 'src/graphql';
import { DIALOGS } from 'src/dialogs/constants';
import { APP_ROLES, COMPANY_TEAMMATE_APP_ROLE_NAMES_LIST, SYSTEM_ROLES } from 'src/types';
import { validateDataByYup, commonErrorResolver, t } from 'src/utils';

interface TeamEditDialogProps {}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: theme.spacing(80),
    minHeight: 537,
    display: 'flex',
    flexDirection: 'column',
  },
  loader: {
    marginBottom: 'auto',
  },
}));

export const TeamEditDialog: React.FC<TeamEditDialogProps> = () => {
  const classes = useStyles();

  const notification = useNotification();

  const { open, closeModal, args, openModal } = useModal(DIALOGS.TEAM_EDIT_DIALOG);

  const { data: userData, loading: userLoading } = useQuery<{ user: User }>(TEAM_EDIT_QUERY, {
    variables: {
      id: args?.userId,
    },
    skip: !open || !Boolean(args?.userId),
  });

  const user = userData?.user;
  const isOwner = user?.roles?.items?.some(role => role.name === SYSTEM_ROLES.appCustomerOwner);

  const userRole = React.useMemo(
    () =>
      user?.roles?.items?.filter(({ name }) =>
        COMPANY_TEAMMATE_APP_ROLE_NAMES_LIST.some(systemRoleName => systemRoleName === name),
      ) || [],
    [user],
  );

  const roleOptions = React.useMemo(
    () =>
      COMPANY_TEAMMATE_APP_ROLE_NAMES_LIST.map(name => {
        const isRoleToDrop =
          !args ||
          (args?.doesCompanyHaveOwner && !isOwner && name === APP_ROLES.appCustomerOwner.name);

        if (isRoleToDrop) {
          return null;
        }

        return { label: APP_ROLES[name].title, value: name };
      }).filter(Boolean),
    [args, isOwner],
  );

  const [eventResolver] = useMutation(EVENT_RESOLVER_MUTATION, {
    refetchQueries: ['UsersTableContent', 'PortfolioCompaniesEntity', 'LoanApplicationsEntity'],
    onError: error => notification.error(commonErrorResolver(error)),
    onCompleted: () => notification.success(t('teammate_update_success')),
    awaitRefetchQueries: true,
  });

  const onSubmit = React.useCallback(
    async (data, form) => {
      await eventResolver({
        variables: {
          event: data,
        },
      });

      closeModal();

      setTimeout(form.reset);
    },
    [closeModal, eventResolver],
  );

  const onClose = React.useCallback(
    (form: any, pristine: boolean) => {
      if (!pristine) {
        openModal(DIALOGS.DATA_LOSS_DIALOG, {
          onConfirm: () => {
            closeModal();

            setTimeout(form.reset);
          },
        });
      } else {
        closeModal();

        setTimeout(form.reset);
      }
    },
    [closeModal, openModal],
  );

  const initialValues = React.useMemo(
    () =>
      userLoading
        ? {}
        : {
            type: EventType.CompanyTeammateEdit,
            payload: {
              companyId: args?.companyId,
              appRoleName: userRole[0]?.name,
              ...user,
            },
          },
    [args, user, userLoading, userRole],
  );

  if (!open) {
    return null;
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={values => validateDataByYup(CompanyTeammateEditEventSchema, values)}
    >
      {({ handleSubmit, form, submitting, pristine }): React.ReactNode => (
        <Dialog
          disableRestoreFocus
          open={open}
          onClose={() => onClose(form, pristine)}
          PaperProps={{
            className: classes.root,
            component: 'form',
            onSubmit: handleSubmit,
          }}
          maxWidth="md"
        >
          <DialogTitle>Edit Teammate</DialogTitle>

          {userLoading ? (
            <LinearProgress className={classes.loader} />
          ) : (
            <DialogContent>
              <Grid container direction="column" spacing={2}>
                <Grid item>
                  <Field
                    component={TextField}
                    name="payload.firstName"
                    label="First Name*"
                    disabled={submitting}
                    fullWidth
                  />
                </Grid>
                <Grid item>
                  <Field
                    component={TextField}
                    name="payload.lastName"
                    label="Last Name*"
                    fullWidth
                  />
                </Grid>
                <Grid item>
                  <Field
                    component={TextField}
                    name="payload.email"
                    label="Email*"
                    disabled
                    fullWidth
                  />
                </Grid>
                <Grid item>
                  <Field
                    component={TextField}
                    name="payload.title"
                    label="Title"
                    disabled={submitting}
                    fullWidth
                  />
                </Grid>
                <Grid item>
                  <Field
                    component={SelectField}
                    name="payload.appRoleName"
                    label="Role*"
                    disabled={submitting}
                    options={roleOptions}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </DialogContent>
          )}

          <DialogActions>
            <Button color="secondary" disabled={submitting} onClick={() => onClose(form, pristine)}>
              Cancel
            </Button>
            <SubmitButton
              color="secondary"
              variant="contained"
              type="submit"
              disabled={pristine}
              loading={submitting}
            >
              Save Changes
            </SubmitButton>
          </DialogActions>
        </Dialog>
      )}
    </Form>
  );
};
